import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  AccountTree,
  PeopleAlt,
  Route,
} from '@mui/icons-material';
import { 
  FaUserShield, 
  FaStopwatch,
  FaProjectDiagram
} from 'react-icons/fa';
import { 
  MdDashboard 
} from 'react-icons/md';
import { 
  CiSaveUp1 
} from 'react-icons/ci';
import SpatialComputingVisualization from '../components/SpatialComputingVisualization';
import { SmartCTA } from '../components/SmartCTAs';
import marketingTheme from '../themes/marketingTheme';

const ProductOverview = () => {
  const productFeatures = [
    {
      title: 'Worker Safety',
      icon: <FaUserShield size={48} color={marketingTheme.palette.primary.main} />,
      link: '/product/worker-safety',
    },
    {
      title: 'Ergonomic Analysis',
      icon: <PeopleAlt sx={{ fontSize: 48, color: marketingTheme.palette.primary.main }} />,
      link: '/product/ergonomic-analytics',
    },
    {
      title: 'Time-and-Motion',
      icon: <FaStopwatch size={48} color={marketingTheme.palette.primary.main} />,
      link: '/product/time-and-motion',
    },
    {
      title: 'Lean Waste Analysis',
      icon: <AccountTree sx={{ fontSize: 48, color: marketingTheme.palette.primary.main }} />,
      link: '/product/lean-waste-analytics',
    },
    {
      title: 'Spaghetti Analysis',
      icon: <Route sx={{ fontSize: 48, color: marketingTheme.palette.primary.main }} />,
      link: '/product/spaghetti-analytics',
    },
  ];

  const handleFeatureClick = (url) => {
    console.log('handleCellClick', url);
    // Analytics tracking
    if (window.gtag) {
      window.gtag('event', 'conversion', { send_to: 'AW-16872295585/O8D7CKns6Z4aEKGZq-0-' });
    }
  };

  return (
    <Box sx={{ backgroundColor: '#24242a', minHeight: '100vh', pt: 12 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '3.5rem', md: '4.2rem' },
            fontWeight: 700,
            color: 'white',
            mb: 2,
          }}
        >
          MotionLogic
          <Box component="span" sx={{ fontSize: '0.5em', color: '#bbb' }}>
            ®
          </Box>{' '}
          {/* Leave this commented out */}
          {/* <Box
            component="span"
            sx={{ color: marketingTheme.palette.primary.main }}
          >
            Platform
          </Box> */}
        </Typography>

        <Typography
          variant="h4"
          sx={{
            fontSize: { xs: '1.2rem', md: '1.5rem' },
            color: marketingTheme.palette.primary.main,
            mb: 2,
          }}
        >
          Unified Spatial Computing for Heavy-Industry
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: '1.1rem',
            color: 'rgba(255, 255, 255, 0.8)',
            maxWidth: '800px',
            lineHeight: 1.6,
            mb: 6,
          }}
        >
          A comprehensive platform ecosystem that unifies spatial intelligence across your industrial operations. 
          Start with any module and expand as your needs grow—each component builds upon the others to create 
          a complete digital transformation solution.
        </Typography>

        {/* 3D Visualization Hero Section */}
        <Box sx={{ 
          mb: 8,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(237, 185, 46, 0.1) 0%, rgba(29, 209, 161, 0.1) 100%)',
          border: '1px solid rgba(237, 185, 46, 0.3)',
        }}>
          {/* Background Pattern */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `radial-gradient(circle at 20px 20px, rgba(237, 185, 46, 0.3) 1px, transparent 0)`,
            backgroundSize: '40px 40px',
            opacity: 0.3,
          }} />
          
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Grid container spacing={6} alignItems="center" sx={{ py: 6 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="h3" sx={{ 
                  color: 'white', 
                  mb: 3,
                  fontWeight: 600
                }}>
                  Visualize Your Industrial Space in 3D
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)', 
                  fontSize: '1.1rem',
                  lineHeight: 1.7,
                  mb: 4
                }}>
                  Transform complex industrial workflows into intuitive 3D spatial insights. Our platform creates 
                  interactive digital twins of your operations, revealing hidden patterns and optimization opportunities 
                  that traditional 2D analytics miss.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: '#1dd1a1' 
                    }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Real-time 3D Tracking
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: '#1dd1a1' 
                    }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Spatial Heat Maps
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: '#1dd1a1' 
                    }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Movement Flow Analysis
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <SpatialComputingVisualization />
              </Grid>
            </Grid>
          </Container>
        </Box>



        {/* Platform Modules - MotionLogic® Insights Panel */}
        <Typography
          variant="h4"
          sx={{
            textAlign: 'center',
            color: 'white',
            fontWeight: 600,
            mb: 5,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: '-15px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '80px',
              height: '3px',
              backgroundColor: marketingTheme.palette.primary.main,
              borderRadius: '2px',
            }
          }}
        >
          MotionLogic® Insights
        </Typography>
        
        <Box sx={{ 
          backgroundColor: 'rgba(45, 45, 51, 0.3)', 
          borderRadius: '12px', 
          p: { xs: 2, md: 4 },
          border: '1px solid rgba(255, 255, 255, 0.05)',
          mb: 8
        }}>
          <Grid container spacing={3} justifyContent="center">
            {productFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} lg={2.4} key={index}>
                <Card
                  component={Link}
                  to={feature.link}
                  onClick={() => handleFeatureClick(feature.link)}
                  sx={{
                    backgroundColor: 'rgba(45, 45, 51, 0.7)',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    borderRadius: 2,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      backgroundColor: 'rgba(237, 185, 46, 0.1)',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                      borderColor: 'rgba(237, 185, 46, 0.3)',
                    },
                  }}
                >
                  <CardContent sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    justifyContent: 'center',
                    p: 3,
                    textAlign: 'center',
                  }}>
                    <Box sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '80px',
                      width: '80px',
                      mb: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.1)',
                      }
                    }}>
                      {feature.icon}
                    </Box>
                    <Typography sx={{ 
                      fontSize: '1rem', 
                      fontWeight: 'bold',
                      fontFamily: 'Roboto, sans-serif',
                      color: 'white'
                    }}>
                      {feature.title}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Platform Integration Benefits */}
        <Container maxWidth="lg" sx={{ mb: 8 }}>
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: '1.8rem', md: '2.2rem' },
              fontWeight: 600,
              color: 'white',
              mb: 4,
              textAlign: 'center',
            }}
          >
            The Benefits of a Unified Platform
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px) scale(1.1)',
                    }
                  }}
                >
                  <FaProjectDiagram size={56} color={marketingTheme.palette.primary.main} />
                </Box>
                <Typography variant="h6" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
                  See the Full Picture
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  When safety data connects with productivity metrics, patterns emerge that individual tools never reveal. 
                  Spot the root cause of production bottlenecks, and understand safety risks in context.
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px) scale(1.1)',
                    }
                  }}
                >
                  <MdDashboard size={56} color={marketingTheme.palette.primary.main} />
                </Box>
                <Typography variant="h6" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
                  One Login, All Insights
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Stop juggling multiple dashboards and training your team on different interfaces. 
                  Everything you need in one familiar place.
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px) scale(1.1)',
                    }
                  }}
                >
                  <CiSaveUp1 size={56} color={marketingTheme.palette.primary.main} />
                </Box>
                <Typography variant="h6" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
                  Grow at Your Pace
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Start with your biggest pain point, prove the value, then expand when you're ready. 
                  Each module builds on what you already know.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>

        {/* Main Content */}
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Paper
              sx={{
                backgroundColor: '#2d2d33',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                p: 4,
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  color: marketingTheme.palette.primary.main,
                  fontSize: '1.8rem',
                  fontWeight: 600,
                  mb: 3,
                }}
              >
                Unified Spatial Computing Platform
              </Typography>
              
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '1.1rem',
                  lineHeight: 1.6,
                  mb: 4,
                }}
              >
                MotionLogic® is more than individual tools—it's a comprehensive platform ecosystem that transforms 
                how you understand and optimize your industrial operations.
                <br /><br />
                Our unified approach means data flows seamlessly between modules, creating compound insights that 
                drive better decisions across productivity, safety, and operational excellence.
                <br /><br />
                Start with any module that addresses your immediate needs, then expand to unlock the full power 
                of spatial intelligence across your facility.
              </Typography>

              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <img
                  src="/images/process_segmentation.png"
                  alt="AI Vision for Production Flow"
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                  }}
                />
              </Box>

              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '1.1rem',
                  lineHeight: 1.6,
                  mb: 4,
                }}
              >
                MotionLogic is building a safer and more productive manufacturing industry by changing the way we see workplace movement.
                <br /><br />
                MotionLogic® includes a self-service event builder, so that production and safety personnel can target key ergonomic events and tool usage events.
              </Typography>

              <Box sx={{ textAlign: 'left', mt: 3, mb: 4 }}>
                <SmartCTA
                  context="how-it-works"
                  fallbackHref="/how-it-works"
                  fallbackText="How it Works"
                  variant="outlined"
                  size="medium"
                  sx={{
                    borderColor: marketingTheme.palette.primary.main,
                    color: marketingTheme.palette.primary.main,
                    fontSize: '1rem',
                    px: 3,
                    py: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(237, 185, 46, 0.1)',
                      borderColor: marketingTheme.palette.primary.dark,
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <img
                  src="/images/worker_ergonomics.png"
                  alt="Worker Ergonomics Analysis"
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                  }}
                />
              </Box>

              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '1.1rem',
                  lineHeight: 1.6,
                  mb: 4,
                }}
              >
                MotionLogic's Scene Understanding technology transforms video into structured datasets. We present these as comprehensible reports, which can be used to inform Lean initiatives.
                <br /><br />
                MotionLogic's algorithms perceive from cameras, enabling continuous, factual, and quantifiable observations of ergonomics in your process. We collate and organise the knowledge into tailored procedure enhancement recommendations.
              </Typography>

              <Typography
                variant="h3"
                sx={{
                  color: marketingTheme.palette.primary.main,
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                Enhanced Awareness
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '1.1rem',
                  lineHeight: 1.6,
                  mb: 4,
                }}
              >
                Waiting and motion are huge sources of industrial inefficiency. MotionLogic can quantify these wastes to improve productivity and safety. Enabling smarter work design and management.
              </Typography>

              <Box sx={{ textAlign: 'left', mt: 4 }}>
                <SmartCTA
                  context="experience-demo"
                  fallbackHref="/contact"
                  fallbackText="Learn More"
                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: marketingTheme.palette.primary.main,
                    color: '#000',
                    fontSize: '1.1rem',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: marketingTheme.palette.primary.dark,
                    },
                  }}
                />
              </Box>

            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ProductOverview;
